import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { getTrailById, getTrailContent } from '../../services/learningTrailService';
import { startTrail, updateProgress, updateContentCompletion, getContentCompletionStatus } from '../../services/studentProgressService';
import ContentRenderer from './components/ContentRenderer';
import ProgressBar from './components/ProgressBar';
import { awardStarsForTrailCompletion } from '../../services/rewardService';

// Trail Completion Score Summary component
const TrailScoreSummary = ({ scoreSummary }) => {
  if (!scoreSummary) return null;
  const { totalCorrect, totalItems, percentage, starsEarned, scoreBreakdown } = scoreSummary;

  return (
    <div className="mt-6 space-y-4">
      {/* Overall Score */}
      <div className={`rounded-xl p-5 border-2 ${
        starsEarned === 3 ? 'bg-yellow-50 border-yellow-300' :
        starsEarned === 2 ? 'bg-green-50 border-green-300': 'bg-blue-50 border-blue-300'
      }`}>
        <h3 className="text-base font-semibold text-foreground mb-3 text-center">Your Trail Score</h3>
        <div className="flex items-center justify-center space-x-1 mb-3">
          {[1, 2, 3]?.map(s => (
            <Icon
              key={s}
              name="Star"
              size={32}
              className={s <= starsEarned ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
            />
          ))}
        </div>
        {totalItems > 0 ? (
          <div className="text-center">
            <div className="text-3xl font-bold text-foreground">{totalCorrect}/{totalItems}</div>
            <div className={`text-lg font-semibold mt-1 ${
              starsEarned === 3 ? 'text-yellow-600' :
              starsEarned === 2 ? 'text-green-600' : 'text-blue-600'
            }`}>{percentage}%</div>
            <div className="text-sm text-muted-foreground mt-1">
              {starsEarned === 3 ? '🏆 Perfect score!' :
               starsEarned === 2 ? '🎉 Great job! 70%+ achieved!': '✅ Trail completed!'}
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">Trail Completed!</div>
            <div className="text-sm text-muted-foreground mt-1">⭐ 1 star earned for completion</div>
          </div>
        )}
      </div>
      {/* Score Breakdown */}
      {scoreBreakdown?.length > 0 && (
        <div className="bg-white rounded-xl border border-border p-4">
          <h4 className="text-sm font-semibold text-foreground mb-3">Score Breakdown</h4>
          <div className="space-y-2">
            {scoreBreakdown?.map((item, idx) => {
              const pct = item?.total > 0 ? Math.round((item?.correct / item?.total) * 100) : 0;
              return (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon
                      name={item?.type === 'quiz' ? 'HelpCircle' : 'Gamepad2'}
                      size={14}
                      className={item?.type === 'quiz' ? 'text-orange-500' : 'text-green-500'}
                    />
                    <span className="text-muted-foreground capitalize">{item?.label}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-foreground">{item?.correct}/{item?.total}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      pct === 100 ? 'bg-yellow-100 text-yellow-700' :
                      pct >= 70 ? 'bg-green-100 text-green-700': 'bg-gray-100 text-gray-600'
                    }`}>{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const StudentLearningTrailView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trailId = searchParams?.get('trailId');
  
  const [studentSession, setStudentSession] = useState(null);
  const [trail, setTrail] = useState(null);
  const [contents, setContents] = useState([]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [completedPages, setCompletedPages] = useState(new Set());
  const [contentCompletionStatus, setContentCompletionStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [textSize, setTextSize] = useState('medium');
  const [highContrast, setHighContrast] = useState(false);
  const [accumulatedScores, setAccumulatedScores] = useState({ quiz_scores: [], game_scores: [] });
  const [trailScoreSummary, setTrailScoreSummary] = useState(null);
  // Ref to always hold the latest accumulatedScores without stale closure issues
  const accumulatedScoresRef = React.useRef({ quiz_scores: [], game_scores: [] });

  // Keep ref in sync with state so completion handler always reads latest scores
  useEffect(() => {
    accumulatedScoresRef.current = accumulatedScores;
  }, [accumulatedScores]);

  useEffect(() => {
    // Check student authentication
    const session = localStorage.getItem('studentSession');
    if (!session) {
      navigate('/student-login', { replace: true });
      return;
    }

    try {
      const parsedSession = JSON.parse(session);
      setStudentSession(parsedSession);
      
      if (trailId) {
        fetchTrailData(parsedSession, trailId);
      } else {
        setError('No trail ID provided');
        setLoading(false);
      }
    } catch (err) {
      console.error('Session parse error:', err);
      navigate('/student-login', { replace: true });
    }
  }, [trailId, navigate]);

  const fetchTrailData = async (session, id) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch trail details
      const { trail: trailData, error: trailError } = await getTrailById(id);
      if (trailError) {
        setError(trailError);
        setLoading(false);
        return;
      }

      // Fetch trail content
      const { contents: contentData, error: contentError } = await getTrailContent(id);
      if (contentError) {
        console.error('Failed to load trail content:', contentError);
        setError('Failed to load trail content');
        setLoading(false);
        return;
      }

      // Transform content data to ensure proper field mapping
      const transformedContent = contentData?.map(item => {
        if (item?.content_type === 'quiz' && item?.content_data) {
          // Map database field names (snake_case) to component field names (camelCase)
          const contentData = { ...item?.content_data };
          if (contentData?.correct_answer !== undefined) {
            contentData.correctAnswer = contentData?.correct_answer;
          }
          return {
            ...item,
            content_data: contentData
          };
        }
        return item;
      }) || [];

      setTrail(trailData);
      setContents(transformedContent);

      // Group content by pages
      const groupedPages = groupContentByPages(transformedContent);
      setPages(groupedPages);

      // Start trail progress tracking
      await startTrail(session?.id, id);

      // Fetch content completion status
      const { completionStatus } = await getContentCompletionStatus(session?.id, id);
      setContentCompletionStatus(completionStatus || {});
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching trail:', err);
      setError('Failed to load trail content');
      setLoading(false);
    }
  };

  const groupContentByPages = (contentData) => {
    // Group content by page_number
    const pageMap = {};
    
    contentData?.forEach(item => {
      const pageNum = item?.page_number || 1;
      if (!pageMap?.[pageNum]) {
        pageMap[pageNum] = [];
      }
      pageMap?.[pageNum]?.push(item);
    });

    // Convert to array and sort by page number
    const pagesArray = Object.keys(pageMap)?.map(Number)?.sort((a, b) => a - b)?.map(pageNum => ({
        pageNumber: pageNum,
        contents: pageMap?.[pageNum]?.sort((a, b) => a?.order_position - b?.order_position),
        isCompletionPage: pageMap?.[pageNum]?.some(item => item?.content_type === 'completion')
      }));

    return pagesArray;
  };

  const handleContentComplete = async (contentId, scoreData) => {
    // Build extra data including score if provided
    const completionData = {
      completed: true,
      completedAt: new Date()?.toISOString(),
      attempts: 1
    };

    // Accumulate scores for star calculation
    if (scoreData) {
      const contentItem = contents?.find(c => c?.id === contentId);
      const contentType = contentItem?.content_type;

      setAccumulatedScores(prev => {
        if (contentType === 'quiz') {
          return {
            ...prev,
            quiz_scores: [...(prev?.quiz_scores || []), { correct: scoreData?.correct, contentId }]
          };
        } else if (contentType === 'game') {
          return {
            ...prev,
            game_scores: [...(prev?.game_scores || []), {
              score: scoreData?.score ?? (scoreData?.correct ? 1 : 0),
              correct: scoreData?.correct ?? (scoreData?.score === 1 ? (scoreData?.total ?? 1) : 0),
              total: scoreData?.total ?? 1,
              gameType: scoreData?.gameType,
              contentId
            }]
          };
        }
        return prev;
      });
    }

    const { success } = await updateContentCompletion(
      studentSession?.id,
      trailId,
      contentId,
      completionData
    );

    if (success) {
      setContentCompletionStatus(prev => ({
        ...prev,
        [contentId]: {
          completed: true,
          completedAt: new Date()?.toISOString(),
          attempts: 1
        }
      }));
    }
  };

  const isPageCompleted = () => {
    // Always allow proceeding - content completion is tracked but not blocking
    return true;
  };

  const handlePageComplete = async () => {
    const newCompleted = new Set(completedPages);
    newCompleted?.add(currentPageIndex);
    setCompletedPages(newCompleted);

    // Calculate completion percentage
    const completionPercentage = Math.round((newCompleted?.size / pages?.length) * 100);
    
    // Check if this is the completion page (last page)
    const currentPageData = pages?.[currentPageIndex];
    const isCompletionPage = currentPageData?.isCompletionPage || currentPageIndex === pages?.length - 1;
    
    // Update progress with completion data
    await updateProgress(studentSession?.id, trailId, {
      completionPercentage: isCompletionPage ? 100 : completionPercentage,
      lastAccessedAt: new Date()?.toISOString(),
      completedAt: isCompletionPage ? new Date()?.toISOString() : null
    });
  };

  const handleNext = async () => {
    if (!completedPages?.has(currentPageIndex)) {
      await handlePageComplete();
    }
    
    if (currentPageIndex < pages?.length - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleExit = () => {
    setShowExitConfirm(true);
  };

  const confirmExit = () => {
    navigate('/student-dashboard');
  };

  const currentPage = pages?.[currentPageIndex];
  const progress = Math.round((completedPages?.size / pages?.length) * 100);
  const isLastPage = currentPageIndex === pages?.length - 1;
  const isFirstPage = currentPageIndex === 0;
  const canProceedToNext = true;

  const computeTrailScoreSummary = (scores) => {
    const quizScores = scores?.quiz_scores || [];
    const gameScores = scores?.game_scores || [];

    const scoreBreakdown = [];

    quizScores?.forEach((q, idx) => {
      scoreBreakdown?.push({
        type: 'quiz',
        label: `Quiz ${idx + 1}`,
        correct: q?.correct ? 1 : 0,
        total: 1
      });
    });

    gameScores?.forEach((g, idx) => {
      const correct = g?.total > 0 ? Math.round(g?.score * g?.total) : (g?.score === 1 ? 1 : 0);
      const total = g?.total || 1;
      scoreBreakdown?.push({
        type: 'game',
        label: g?.gameType ? g?.gameType?.replace('-', ' ') : `Game ${idx + 1}`,
        correct,
        total
      });
    });

    const totalCorrect = scoreBreakdown?.reduce((sum, s) => sum + s?.correct, 0);
    const totalItems = scoreBreakdown?.reduce((sum, s) => sum + s?.total, 0);
    const percentage = totalItems > 0 ? Math.round((totalCorrect / totalItems) * 100) : 0;

    let starsEarned = 1;
    if (totalItems > 0) {
      if (percentage === 100) starsEarned = 3;
      else if (percentage >= 70) starsEarned = 2;
    }

    return { totalCorrect, totalItems, percentage, starsEarned, scoreBreakdown };
  };

  // Mark trail as completed when the completion page is reached
  useEffect(() => {
    if (!studentSession?.id || !trailId || !currentPage) return;
    if (currentPage?.isCompletionPage) {
      updateProgress(studentSession?.id, trailId, {
        completionPercentage: 100,
        completedAt: new Date()?.toISOString(),
        lastAccessedAt: new Date()?.toISOString()
      })?.then(() => {
        // Use the ref to get the latest accumulated scores (avoids stale closure)
        const latestScores = accumulatedScoresRef?.current;
        // Use only the current session scores — do NOT merge with old DB data
        // Merging old scores inflates totalPoints and reduces the percentage incorrectly
        const currentSessionAnswers = {
          quiz_scores: latestScores?.quiz_scores || [],
          game_scores: latestScores?.game_scores || []
        };
        awardStarsForTrailCompletion(studentSession?.id, trailId, currentSessionAnswers);
        // Compute and display score summary
        const summary = computeTrailScoreSummary(currentSessionAnswers);
        setTrailScoreSummary(summary);
      });
    }
  }, [currentPage?.isCompletionPage, studentSession?.id, trailId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm sm:text-base text-muted-foreground">Loading trail...</p>
        </div>
      </div>
    );
  }

  if (error || !trail) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center max-w-sm sm:max-w-md mx-auto px-4">
          <Icon name="AlertCircle" size={40} className="text-error mx-auto mb-4 sm:w-12 sm:h-12" />
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Trail Not Found</h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-6">{error || 'The requested trail could not be found.'}</p>
          <Button onClick={() => navigate('/student-dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{trail?.title} - LearningTrails</title>
        <meta name="description" content={trail?.description} />
      </Helmet>
      <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 ${highContrast ? 'high-contrast' : ''}`}>
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 py-1 sm:py-1.5">
            <div className="flex items-center justify-between">
              <button
                onClick={handleExit}
                className="flex items-center space-x-1 text-muted-foreground hover:text-foreground transition-organic"
              >
                <Icon name="X" size={14} />
                <span className="text-xs font-medium hidden xs:inline">Exit</span>
              </button>

              <h1 className="text-xs sm:text-sm font-semibold text-foreground truncate flex-1 text-center mx-2">{trail?.title}</h1>

              <div className="flex items-center space-x-1 sm:space-x-2">
                <span className="text-xs text-muted-foreground mr-1">
                  {currentPageIndex + 1}/{pages?.length}
                </span>
                <button
                  onClick={() => setTextSize(textSize === 'small' ? 'medium' : textSize === 'medium' ? 'large' : 'small')}
                  className="p-0.5 sm:p-1 text-muted-foreground hover:text-foreground transition-organic"
                  title="Text Size"
                >
                  <Icon name="Type" size={14} />
                </button>
                <button
                  onClick={() => setHighContrast(!highContrast)}
                  className="p-0.5 sm:p-1 text-muted-foreground hover:text-foreground transition-organic"
                  title="High Contrast"
                >
                  <Icon name="Contrast" size={14} />
                </button>
              </div>
            </div>

            <ProgressBar progress={progress} />
          </div>
        </div>

        {/* Main Content */}
        <div className="pt-14 sm:pt-16 pb-24 sm:pb-28">
          <div className="w-full px-3 sm:px-4 md:px-6">
            {currentPage?.isCompletionPage ? (
              <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6 md:p-8 mb-6 max-w-lg sm:max-w-xl md:max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-success/10 rounded-full mb-4 sm:mb-6">
                    <Icon name="Award" size={36} className="text-success sm:w-12 sm:h-12" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 sm:mb-4">
                    {currentPage?.contents?.[0]?.content_data?.title || 'Congratulations!'}
                  </h2>
                  <p className="text-base sm:text-lg text-muted-foreground mb-4 sm:mb-6 whitespace-pre-wrap">
                    {currentPage?.contents?.[0]?.content_data?.message || 'You have completed this learning trail!'}
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-success mb-4 sm:mb-6">
                    <Icon name="CheckCircle2" size={20} />
                    <span className="text-base sm:text-lg font-semibold">Trail Completed!</span>
                  </div>
                  {/* Trail Score Summary */}
                  <TrailScoreSummary scoreSummary={trailScoreSummary} />
                  <div className="mt-6">
                    <Button onClick={() => navigate('/student-dashboard')} iconName="Home" size="lg">
                      Back to Homepage
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {currentPage?.contents?.map((content, idx) => (
                  <div key={content?.id || idx} className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
                    <ContentRenderer 
                      content={content} 
                      textSize={textSize}
                      onComplete={(scoreData) => handleContentComplete(content?.id, scoreData)}
                      isCompleted={contentCompletionStatus?.[content?.id]?.completed === true}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border shadow-lg">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-2">
              <Button
                onClick={handlePrevious}
                disabled={isFirstPage}
                variant="outline"
                iconName="ChevronLeft"
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </Button>
              
              {!canProceedToNext && (
                <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-muted-foreground">
                  <Icon name="Info" size={14} />
                  <span className="hidden sm:inline">Complete all content to proceed</span>
                </div>
              )}
              
              <Button
                onClick={handleNext}
                disabled={isLastPage}
                iconName="ChevronRight"
                iconPosition="right"
              >
                {isLastPage ? 'Finish' : 'Next'}
              </Button>
            </div>
          </div>
        </div>

        {/* Exit Confirmation Modal */}
        {showExitConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm sm:max-w-md w-full p-5 sm:p-6">
              <div className="flex items-start space-x-3 sm:space-x-4 mb-5 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={20} className="text-warning" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-1 sm:mb-2">Exit Learning Trail?</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">
                    Your progress has been saved. You can continue from where you left off later.
                  </p>
                </div>
              </div>
              <div className="flex space-x-2 sm:space-x-3">
                <Button
                  onClick={() => setShowExitConfirm(false)}
                  variant="outline"
                  fullWidth
                >
                  Continue
                </Button>
                <Button
                  onClick={confirmExit}
                  variant="destructive"
                  fullWidth
                >
                  Exit
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StudentLearningTrailView;